// @ts-nocheck
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

export class ComplementaryProducts extends Component {
  #selectedProducts = new Set();
  #productPrices = new Map();
  #productsInCart = new Set();
  #unavailableProducts = new Set();
  #inventoryExhaustedProducts = new Set();
  #cartVariantQuantities = new Map();
  #currentProductId = null;
  #currentVariantId = null;
  #isVariantSelected = false;
  #abortController = new AbortController();
  #variantCheckInterval = null;
  #cartSyncTimer = null;
  #fetchInterceptCallback = null;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#variantCheckInterval) {
      clearInterval(this.#variantCheckInterval);
    }
    if (this.#cartSyncTimer) {
      clearTimeout(this.#cartSyncTimer);
    }
    // Remove this instance's callback from global cart interceptor
    if (this.#fetchInterceptCallback && window.__complementaryCartCallbacks) {
      window.__complementaryCartCallbacks.delete(this.#fetchInterceptCallback);
    }
  }

  #initialize() {
    this.#getCurrentProductId();
    this.#getCurrentVariantId();
    this.#loadInitialState();
    this.#setupEventListeners();
    this.#setupCartChangeDetection();
    this.#fetchCartData().then(() => {
      this.#syncCartSelectionWithCards();
    });
    this.#updateTotal();

    setTimeout(() => {
      this.#getCurrentVariantId();

      // Try to get variant data from the variant picker if a variant is selected
      let variantData = null;
      if (this.#currentVariantId) {
        const variantPicker = document.querySelector('variant-picker:not(product-card variant-picker):not(quick-add-dialog variant-picker)');
        if (variantPicker) {
          const variantScript = variantPicker.querySelector('script[type="application/json"]');
          if (variantScript) {
            try {
              variantData = JSON.parse(variantScript.textContent);
              // Only use if it matches current variant
              if (variantData.id?.toString() !== this.#currentVariantId?.toString()) {
                variantData = null;
              }
            } catch (e) {
              console.warn('Failed to parse variant data on init:', e);
            }
          }
        }
      }

      this.#updateCurrentProductCardState(variantData);
    }, 100);
  }

  #getCurrentProductId() {
    const productForm = document.querySelector('product-form-component');
    if (productForm) {
      const productIdInput = productForm.querySelector('input[name="product_id"]');
      if (productIdInput) {
        this.#currentProductId = productIdInput.value;
        return;
      }
    }
  }

  #getCurrentVariantId() {
    const variantPickerForm = document.querySelector('variant-picker form:not(product-card variant-picker form):not(quick-add-dialog variant-picker form)');

    if (variantPickerForm) {
      const checkedRadio = variantPickerForm.querySelector('input[type="radio"]:checked[data-variant-id]');
      if (checkedRadio && checkedRadio.dataset.variantId) {
        this.#currentVariantId = checkedRadio.dataset.variantId;
        this.#isVariantSelected = true;
        return;
      }

      const currentCheckedRadio = variantPickerForm.querySelector('input[type="radio"][data-current-checked="true"][data-variant-id]');
      if (currentCheckedRadio && currentCheckedRadio.dataset.variantId) {
        this.#currentVariantId = currentCheckedRadio.dataset.variantId;
        this.#isVariantSelected = true;
        return;
      }
    }

    const variantPicker = document.querySelector('variant-picker:not(product-card variant-picker):not(quick-add-dialog variant-picker)');
    if (variantPicker) {
      const checkedRadio = variantPicker.querySelector('input[type="radio"]:checked[data-variant-id]');
      if (checkedRadio && checkedRadio.dataset.variantId) {
        this.#currentVariantId = checkedRadio.dataset.variantId;
        this.#isVariantSelected = true;
        return;
      }
    }

    const allVariantPickers = document.querySelectorAll('variant-picker');
    for (const picker of allVariantPickers) {
      if (picker.closest('product-card') || picker.closest('quick-add-dialog')) {
        continue;
      }
      const checkedRadio = picker.querySelector('input[type="radio"]:checked[data-variant-id]');
      if (checkedRadio && checkedRadio.dataset.variantId) {
        this.#currentVariantId = checkedRadio.dataset.variantId;
        this.#isVariantSelected = true;
        return;
      }
    }

    const variantInput = document.querySelector('input[name="id"][type="hidden"]');
    if (variantInput && variantInput.value) {
      const hasCheckedRadio = document.querySelector('variant-picker input[type="radio"]:checked[data-variant-id]');
      if (hasCheckedRadio) {
        this.#currentVariantId = variantInput.value;
        this.#isVariantSelected = true;
        return;
      }
    }

    const productForm = document.querySelector('product-form-component');
    if (productForm) {
      const variantIdInput = productForm.querySelector('input[name="id"]');
      if (variantIdInput && variantIdInput.value) {
        const hasCheckedRadio = document.querySelector('variant-picker input[type="radio"]:checked[data-variant-id]');
        if (hasCheckedRadio) {
          this.#currentVariantId = variantIdInput.value;
          this.#isVariantSelected = true;
          return;
        }
      }
    }

    const urlParams = new URLSearchParams(window.location.search);
    const variantParam = urlParams.get('variant');
    if (variantParam) {
      const hasCheckedRadio = document.querySelector('variant-picker input[type="radio"]:checked[data-variant-id]');
      if (hasCheckedRadio) {
        this.#currentVariantId = variantParam;
        this.#isVariantSelected = true;
        return;
      }
    }

    this.#currentVariantId = null;
    this.#isVariantSelected = false;
  }

  #loadInitialState() {
    if (!this.refs.productCard) return;

    this.refs.productCard.forEach((card) => {
      const productId = card.dataset.productId;
      const price = parseInt(card.dataset.productPrice, 10);
      const isCurrent = card.dataset.isCurrent === 'true';
      const button = card.querySelector('.complementary-product-card__select-button');
      const isUnavailable = button?.disabled || button?.getAttribute('aria-disabled') === 'true';

      // Check if all inventory is already in the cart
      const isExhaustedInCart = this.#isInventoryExhaustedInCart(card);

      if (productId) {
        this.#productPrices.set(productId, price);

        if (isExhaustedInCart) {
          this.#inventoryExhaustedProducts.add(productId);
          this.#unavailableProducts.add(productId);
          this.#selectedProducts.delete(productId);
          this.#updateButtonState(card, isCurrent, false, true);
        } else if (isUnavailable) {
          this.#unavailableProducts.add(productId);
        } else {
          this.#unavailableProducts.delete(productId);
        }

        if (isCurrent && !this.#currentProductId) {
          this.#currentProductId = productId;
        }

        // Skip selection logic if inventory is exhausted in cart
        if (isExhaustedInCart) return;

        if (isCurrent) {
          if (this.#isVariantSelected && !isUnavailable) {
            this.#selectedProducts.add(productId);
            this.#updateButtonState(card, true, true);
          } else {
            this.#selectedProducts.delete(productId);
            this.#updateButtonState(card, true, false);
            card.dataset.selected = 'false';
          }
        } else if (card.dataset.selected === 'true' && !isUnavailable) {
          this.#selectedProducts.add(productId);
          this.#updateButtonState(card, false, true);
        } else {
          this.#selectedProducts.delete(productId);
          this.#updateButtonState(card, false, false);
          card.dataset.selected = 'false';
        }
      }
    });
  }

  #setupEventListeners() {
    if (!this.refs.selectButton) return;

    this.refs.selectButton.forEach((button) => {
      button.addEventListener('click', this.#handleSelectClick.bind(this));
    });

    if (this.refs.buyButton) {
      this.refs.buyButton.addEventListener('click', this.#handleBuyClick.bind(this));
    }

    if (this.refs.footer) {
      this.refs.footer.addEventListener('click', (event) => {
        if (event.target === this.refs.buyButton || event.target.closest('.complementary-products__buy-button')) {
          return;
        }
        this.#handleBuyClick(event);
      });
    }

    document.addEventListener(
      ThemeEvents.variantSelected,
      this.#handleVariantSelected.bind(this),
      { signal: this.#abortController.signal }
    );

    document.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate.bind(this),
      { signal: this.#abortController.signal }
    );

    document.addEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartUpdate.bind(this),
      { signal: this.#abortController.signal }
    );

    this.#variantCheckInterval = setInterval(() => {
      this.#checkVariantState();
    }, 1000);
  }

  #setupCartChangeDetection() {
    // Intercept global fetch to detect cart changes from ANY source
    // (cart drawer, cart page, third-party apps, etc.)
    if (!window.__complementaryCartFetchPatched) {
      const originalFetch = window.fetch;
      window.__complementaryCartCallbacks = new Set();

      window.fetch = function (...args) {
        const result = originalFetch.apply(this, args);
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';

        if (/\/cart\/(change|update|clear|add)/.test(url)) {
          result
            .then(() => {
              // Notify all registered complementary-products instances
              window.__complementaryCartCallbacks.forEach((cb) => {
                try {
                  cb();
                } catch (e) {
                  console.warn('Cart intercept callback error:', e);
                }
              });
            })
            .catch(() => {});
        }

        return result;
      };

      window.__complementaryCartFetchPatched = true;
    }

    // Register a debounced callback for THIS instance
    this.#fetchInterceptCallback = () => {
      this.#scheduleCartSync();
    };
    window.__complementaryCartCallbacks.add(this.#fetchInterceptCallback);

    // Also refresh when user returns to this tab (may have modified cart elsewhere)
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.visibilityState === 'visible') {
          this.#scheduleCartSync();
        }
      },
      { signal: this.#abortController.signal }
    );
  }

  #scheduleCartSync() {
    // Debounce so rapid cart operations only trigger one sync
    clearTimeout(this.#cartSyncTimer);
    this.#cartSyncTimer = setTimeout(async () => {
      await this.#fetchCartData();
      this.#syncCartSelectionWithCards();
    }, 500);
  }

  #checkVariantState() {
    const previousVariantId = this.#currentVariantId;
    const previousIsSelected = this.#isVariantSelected;

    this.#getCurrentVariantId();

    if (previousVariantId !== this.#currentVariantId || previousIsSelected !== this.#isVariantSelected) {
      // Try to get variant data from the variant picker
      const variantPicker = document.querySelector('variant-picker:not(product-card variant-picker):not(quick-add-dialog variant-picker)');
      let variantData = null;

      if (variantPicker && this.#currentVariantId) {
        const variantScript = variantPicker.querySelector('script[type="application/json"]');
        if (variantScript) {
          try {
            variantData = JSON.parse(variantScript.textContent);
            // Only use if it matches current variant
            if (variantData.id?.toString() !== this.#currentVariantId?.toString()) {
              variantData = null;
            }
          } catch (e) {
            console.warn('Failed to parse variant data:', e);
          }
        }
      }

      this.#updateCurrentProductCardState(variantData);
    }
  }

  #updateCurrentProductCardState(variantData = null) {
    if (!this.refs.productCard || !this.#currentProductId) return;

    this.refs.productCard.forEach((card) => {
      const productId = card.dataset.productId;
      const isCurrent = card.dataset.isCurrent === 'true';

      if (isCurrent && productId === this.#currentProductId) {
        // Update variant data if provided
        if (variantData) {
          const variantId = variantData.id?.toString();
          const variantPrice = variantData.price ? parseInt(variantData.price, 10) : null;
          const variantAvailable = variantData.available !== false;

          if (variantId) {
            card.dataset.variantId = variantId;
            this.#currentVariantId = variantId;
          }

          if (variantPrice !== null) {
            card.dataset.productPrice = variantPrice.toString();
            this.#productPrices.set(productId, variantPrice);

            // Update displayed price
            const priceElement = card.querySelector('.complementary-product-card__price');
            if (priceElement) {
              priceElement.textContent = this.#formatPrice(variantPrice);
            }
          }

          // ── FIX: Update inventory data attributes when variant changes ──
          // The variant events (VariantSelectedEvent / VariantUpdateEvent) do NOT
          // include inventory_quantity or inventory_policy in their resource.
          // We MUST fall back to window.__variantInventory (Liquid-injected in
          // buy-buttons.liquid) which has accurate data for ALL variants.

          // 1) Try event data first
          let invQty = variantData.inventory_quantity;
          let invPolicy = variantData.inventory_policy;
          let invManagement = variantData.inventory_management;

          // 2) Fall back to the Liquid-injected global for missing fields
          if (variantId && window.__variantInventory) {
            const liquidInv = window.__variantInventory[variantId] || window.__variantInventory[variantId.toString()];
            if (liquidInv) {
              if (invQty === undefined || invQty === null) {
                invQty = liquidInv.quantity;
              }
              if (invPolicy === undefined || invPolicy === null) {
                invPolicy = liquidInv.policy;
              }
              if (invManagement === undefined) {
                invManagement = liquidInv.managed ? 'shopify' : null;
              }
            }
          }

          // 3) Apply resolved values to the card data attributes
          if (invQty !== undefined && invQty !== null) {
            card.dataset.variantInventory = invQty.toString();
          }
          if (invPolicy !== undefined && invPolicy !== null) {
            card.dataset.variantInventoryPolicy = invPolicy;
          }
          if (invManagement !== undefined) {
            const isManaged = invManagement !== null && invManagement !== '';
            card.dataset.variantInventoryManaged = isManaged.toString();
          }

          // Update button availability state
          const button = card.querySelector('.complementary-product-card__select-button');

          if (button) {
            if (!variantAvailable) {
              button.disabled = true;
              button.setAttribute('aria-disabled', 'true');
              this.#unavailableProducts.add(productId);
              // Remove from selected products if it becomes unavailable
              this.#selectedProducts.delete(productId);
            } else {
              button.disabled = false;
              button.removeAttribute('aria-disabled');
              this.#unavailableProducts.delete(productId);
            }
          }
        } else if (this.#currentVariantId) {
          // variantData is null (e.g. from #checkVariantState polling) but we
          // know the current variant ID — sync card attributes from __variantInventory
          const currentVarId = this.#currentVariantId.toString();
          if (card.dataset.variantId !== currentVarId) {
            card.dataset.variantId = currentVarId;
          }
          if (window.__variantInventory) {
            const liquidInv = window.__variantInventory[currentVarId] || window.__variantInventory[currentVarId.toString()];
            if (liquidInv) {
              card.dataset.variantInventory = liquidInv.quantity.toString();
              card.dataset.variantInventoryPolicy = liquidInv.policy;
              card.dataset.variantInventoryManaged = liquidInv.managed.toString();
            }
          }
        }

        // Check if all inventory is already in cart after variant update
        const isExhaustedInCart = this.#isInventoryExhaustedInCart(card);
        // When data-variant-id changes, check again if this variant is in cart and update button state
        const variantInCart = this.#isInCart(card.dataset.variantId?.toString());

        if (isExhaustedInCart) {
          this.#inventoryExhaustedProducts.add(productId);
          this.#unavailableProducts.add(productId);
          this.#selectedProducts.delete(productId);
          this.#updateButtonState(card, true, false, true, false);
        } else if (variantInCart) {
          this.#inventoryExhaustedProducts.delete(productId);
          this.#selectedProducts.delete(productId);
          this.#updateButtonState(card, true, false, false, true);
        } else if (this.#isVariantSelected) {
          this.#inventoryExhaustedProducts.delete(productId);
          if (!this.#unavailableProducts.has(productId)) {
            this.#selectedProducts.add(productId);
            this.#updateButtonState(card, true, true, false, false);
          } else {
            this.#selectedProducts.delete(productId);
            this.#updateButtonState(card, true, false, false, false);
          }

          const variantPicker = document.querySelector('variant-picker:not(product-card variant-picker):not(quick-add-dialog variant-picker)');
          if (variantPicker) {
            const variantError = variantPicker.querySelector('[data-variant-error]');
            if (variantError) {
              variantError.classList.add('hidden');
            }
          }
        } else {
          this.#selectedProducts.delete(productId);
          this.#updateButtonState(card, true, false, false, false);
        }
        this.#updateTotal();
      }
    });
  }

  #handleVariantSelected(event) {
    const variantResource = event.detail?.resource;

    if (variantResource?.id) {
      this.#currentVariantId = variantResource.id;
      this.#isVariantSelected = true;

      // Update the first complementary product card with new variant data
      this.#updateCurrentProductCardState(variantResource);
    } else {
      this.#currentVariantId = null;
      this.#isVariantSelected = false;
      this.#updateCurrentProductCardState();
    }
  }

  #handleVariantUpdate(event) {
    const productId = event.detail?.data?.productId;
    if (productId && productId.toString() !== this.#currentProductId?.toString()) {
      return;
    }

    const variantResource = event.detail?.resource;

    if (variantResource?.id) {
      this.#currentVariantId = variantResource.id;
      this.#isVariantSelected = true;

      // Update the first complementary product card with new variant data
      this.#updateCurrentProductCardState(variantResource);
    } else {
      this.#getCurrentVariantId();
      this.#updateCurrentProductCardState();
    }
  }

  async #handleCartUpdate(event) {
    // Use debounced sync to avoid double-processing with fetch interceptor
    this.#scheduleCartSync();
  }

  #isInventoryExhaustedInCart(card) {
    const variantId = card.dataset.variantId;

    // ── Always reconcile card inventory from __variantInventory (Liquid source of truth) ──
    if (variantId && window.__variantInventory) {
      const liquidInv = window.__variantInventory[variantId] || window.__variantInventory[variantId.toString()];
      if (liquidInv) {
        card.dataset.variantInventory = liquidInv.quantity.toString();
        card.dataset.variantInventoryPolicy = liquidInv.policy;
        card.dataset.variantInventoryManaged = liquidInv.managed.toString();
      }
    }

    const inventoryManaged = card.dataset.variantInventoryManaged === 'true';
    const inventoryPolicy = card.dataset.variantInventoryPolicy;
    const inventoryQty = parseInt(card.dataset.variantInventory, 10);

    // If inventory is not managed or policy allows overselling, never exhausted
    if (!inventoryManaged || inventoryPolicy === 'continue') {
      return false;
    }
    if (isNaN(inventoryQty)) {
      return false;
    }

    const qtyInCart = this.#cartVariantQuantities.get(variantId?.toString()) || 0;
    return qtyInCart >= inventoryQty;
  }

  /**
   * Runs only when cart drawer has been updated (after fetchCartData / cart update).
   * Uses #isInCart (window.latestCartData.items) and updates each card's button state.
   */
  #syncCartSelectionWithCards() {
    if (!this.refs.productCard) {
      return;
    }

    this.refs.productCard.forEach((card) => {
      const productId = card.dataset.productId;
      const variantId = card.dataset.variantId?.toString();
      const isCurrent = card.dataset.isCurrent === 'true';
      const button = card.querySelector('.complementary-product-card__select-button');

      // Only when cart drawer is updated: check if this variant is in latestCartData.items
      const variantInCart = this.#isInCart(variantId);

      const isExhaustedInCart = this.#isInventoryExhaustedInCart(card);
      const wasPreviouslyExhausted = this.#inventoryExhaustedProducts.has(productId);

      if (isExhaustedInCart) {
        this.#inventoryExhaustedProducts.add(productId);
        this.#unavailableProducts.add(productId);
        this.#selectedProducts.delete(productId);
        this.#updateButtonState(card, isCurrent, false, true, false);
      } else {
        if (wasPreviouslyExhausted && button) {
          button.disabled = false;
          button.removeAttribute('aria-disabled');
          card.dataset.soldOut = 'false';
        }

        this.#inventoryExhaustedProducts.delete(productId);

        const isUnavailable = button?.disabled || button?.getAttribute('aria-disabled') === 'true';

        if (isUnavailable) {
          this.#unavailableProducts.add(productId);
          this.#selectedProducts.delete(productId);
        } else {
          this.#unavailableProducts.delete(productId);
        }

        if (isCurrent && productId) {
          if (variantInCart && !isUnavailable) {
            this.#selectedProducts.delete(productId);
            this.#updateButtonState(card, true, false, false, true);
          } else if (wasPreviouslyExhausted && !isUnavailable && this.#isVariantSelected) {
            this.#selectedProducts.add(productId);
            this.#updateButtonState(card, true, true, false, false);
          } else if (isUnavailable) {
            this.#selectedProducts.delete(productId);
            this.#updateButtonState(card, true, false, false, false);
          } else {
            this.#updateButtonState(card, true, this.#selectedProducts.has(productId), false, false);
          }
        } else if (!isCurrent && productId) {
          const isSelected = this.#selectedProducts.has(productId);

          if (variantInCart && !isUnavailable) {
            this.#selectedProducts.delete(productId);
            this.#updateButtonState(card, false, false, false, true);
          } else if (wasPreviouslyExhausted && !isUnavailable) {
            this.#selectedProducts.add(productId);
            this.#updateButtonState(card, false, true, false, false);
          } else if (isSelected || isUnavailable) {
            this.#selectedProducts.delete(productId);
            this.#updateButtonState(card, false, false, false, false);
            card.dataset.selected = 'false';
          } else {
            this.#updateButtonState(card, false, isSelected, false, false);
          }
        }
      }
    });

    this.#updateTotal();
  }

  async #fetchCartData() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return;

      const cart = await response.json();
      window.latestCartData = cart;
      this.#productsInCart.clear();
      this.#cartVariantQuantities.clear();

      if (cart.items && Array.isArray(cart.items)) {
        cart.items.forEach((item) => {
          if (item.product_id) {
            this.#productsInCart.add(item.product_id.toString());
          }
          if (item.variant_id) {
            const variantId = item.variant_id.toString();
            const existing = this.#cartVariantQuantities.get(variantId) || 0;
            this.#cartVariantQuantities.set(variantId, existing + item.quantity);
          }
        });
      }
    } catch (error) {
      console.error('Failed to fetch cart data:', error);
    }
  }

  #handleSelectClick(event) {
    const button = event.currentTarget;
    const productId = button.dataset.productId;

    if (!productId) return;

    const card = button.closest('.complementary-product-card');
    if (!card) return;

    // Prevent selection if product is unavailable
    if (button.disabled || button.getAttribute('aria-disabled') === 'true') {
      return;
    }

    const isCurrent = card.dataset.isCurrent === 'true';
    const isSelected = this.#selectedProducts.has(productId);
    const isInCart = this.#productsInCart.has(productId);

    if (isCurrent) {
      if (this.#isVariantSelected && isSelected) {
        return;
      }

      if (!this.#isVariantSelected) {
        this.#getCurrentVariantId();

        if (!this.#isVariantSelected) {
          const variantPicker = document.querySelector('variant-picker:not(product-card variant-picker):not(quick-add-dialog variant-picker)');

          if (variantPicker) {
            variantPicker.scrollIntoView({ behavior: 'smooth', block: 'center' });

            const variantError = variantPicker.querySelector('[data-variant-error]');
            if (variantError) {
              variantError.textContent = 'Please Select a Size';
              variantError.classList.remove('hidden');

              setTimeout(() => {
                if (variantError && !this.#isVariantSelected) {
                  variantError.classList.add('hidden');
                }
              }, 5000);
            }

            variantPicker.style.outline = '2px solid #EE3224';
            variantPicker.style.outlineOffset = '4px';
            setTimeout(() => {
              variantPicker.style.outline = '';
              variantPicker.style.outlineOffset = '';
            }, 2000);
          }

          return;
        }
      }

      if (this.#isVariantSelected && !isSelected) {
        this.#selectedProducts.add(productId);
        this.#updateButtonState(card, true, true);
        this.#updateTotal();
        card.dataset.selected = 'true';
      }
    } else {
      if (isInCart && isSelected) {
        return;
      }

    if (isSelected) {
      this.#selectedProducts.delete(productId);
        this.#updateButtonState(card, false, false);
    } else {
      this.#selectedProducts.add(productId);
        this.#updateButtonState(card, false, true);
    }

    this.#updateTotal();
    card.dataset.selected = this.#selectedProducts.has(productId) ? 'true' : 'false';
  }
  }

  async #handleBuyClick(event) {
    let button = event.currentTarget;

    if (!button || !button.classList?.contains('complementary-products__buy-button')) {
      button = this.refs.buyButton;
    }

    if (!button) {
      return;
    }

    if (button.disabled) {
      return;
    }
    button.disabled = true;

    const originalText = button.textContent || 'Select and Buy';
    button.textContent = 'Adding...';

    try {
      // ── FIX: Fetch fresh cart data BEFORE building the add list ──
      // This ensures #cartVariantQuantities is up-to-date for the pre-flight inventory check.
      await this.#fetchCartData();

      const productsToAdd = [];

      if (!this.refs.productCard) {
        throw new Error('No product cards found');
      }

      this.refs.productCard.forEach((card) => {
        const productId = card.dataset.productId;
        const isCurrent = card.dataset.isCurrent === 'true';

        // Skip unavailable or inventory-exhausted products
        if (this.#unavailableProducts.has(productId) || this.#inventoryExhaustedProducts.has(productId)) {
          return;
        }

        if (productId && this.#selectedProducts.has(productId)) {
          let variantId = null;

          if (isCurrent) {
            if (!this.#isVariantSelected || !this.#currentVariantId) {
              const variantPicker = document.querySelector('variant-picker:not(product-card variant-picker):not(quick-add-dialog variant-picker)');
              if (variantPicker) {
                variantPicker.scrollIntoView({ behavior: 'smooth', block: 'center' });
                const variantError = variantPicker.querySelector('[data-variant-error]');
                if (variantError) {
                  variantError.textContent = 'Please Select a Size';
                  variantError.classList.remove('hidden');
                }
              }
              throw new Error('Please select a variant for the current product');
            }
            variantId = this.#currentVariantId;
          } else {
            variantId = card.dataset.variantId;
            if (!variantId) {
              return;
            }
          }

          // ── FIX: Refresh inventory from __variantInventory before pre-flight check ──
          // The variant events may not include inventory_quantity, so the card
          // data-attributes could still be stale. Always reconcile here.
          if (variantId && window.__variantInventory) {
            const liquidInv = window.__variantInventory[variantId] || window.__variantInventory[variantId.toString()];
            if (liquidInv) {
              card.dataset.variantInventory = liquidInv.quantity.toString();
              card.dataset.variantInventoryPolicy = liquidInv.policy;
              card.dataset.variantInventoryManaged = liquidInv.managed.toString();
            }
          }

          // ── FIX: Pre-flight inventory check ──
          // Before adding, verify this variant is not already at max inventory in cart.
          const isExhausted = this.#isInventoryExhaustedInCart(card);
          if (isExhausted) {
            // Mark as exhausted immediately so UI updates
            this.#inventoryExhaustedProducts.add(productId);
            this.#unavailableProducts.add(productId);
            this.#selectedProducts.delete(productId);
            this.#updateButtonState(card, isCurrent, false, true);
            return;
          }

          productsToAdd.push({
            productId,
            variantId,
            quantity: 1
          });
        }
      });

      if (productsToAdd.length === 0) {
        // If all selected products were blocked by inventory, sync and update UI
        this.#syncCartSelectionWithCards();
        this.#updateTotal();
        throw new Error('No products available to add — inventory may be exhausted');
      }

      for (const product of productsToAdd) {
        await this.#addToCart(product.variantId, product.quantity);
      }
      // ── Fetch full cart first so `window.latestCartData` is correct before drawer re-renders / deferred sync ──
      await this.#fetchCartData();
      this.#syncCartSelectionWithCards();

      const deferSyncCartDrawer = !!document.querySelector('cart-items-component');
      if (deferSyncCartDrawer && typeof globalThis.requestSyncCartDrawerAfterSectionRender === 'function') {
        globalThis.requestSyncCartDrawerAfterSectionRender();
      }

      const cartUpdateEvent = new CartUpdateEvent(
        window.latestCartData || {},
        this.id || 'complementary-products',
        { itemCount: window.latestCartData?.item_count ?? 0 }
      );
      document.dispatchEvent(cartUpdateEvent);

      if (!deferSyncCartDrawer && typeof globalThis.syncCartDrwer === 'function') {
        globalThis.syncCartDrwer();
      }

      // try {
      //   if (typeof window.activateShowAllButton === 'function') {
      //     window.activateShowAllButton();
      //   }
      //   const upsellEl = document.querySelector('upsell-products');
      //   if (upsellEl && typeof upsellEl.connectedCallback === 'function') {
      //     upsellEl.connectedCallback();
      //   }
      // } catch (postAddError) {
      //   console.warn('Post-add callback error:', postAddError);
      // }

      button.textContent = 'Added to Cart!';
      setTimeout(() => {
        button.textContent = originalText;
        // Only re-enable if there are still purchasable selected products
        const hasAvailableSelected = [...this.#selectedProducts].some(
          pid => !this.#unavailableProducts.has(pid) && !this.#inventoryExhaustedProducts.has(pid)
        );
        if (hasAvailableSelected) {
          button.disabled = false;
        } else {
          button.disabled = true;
        }
      }, 2000);

    } catch (error) {
      console.error('Failed to add products to cart:', error);
      button.textContent = originalText;
      button.disabled = false;

      if (error.message && !error.message.includes('No products available to add')) {
        console.error(error.message);
      } else if (!error.message) {
        console.error('Failed to add products to cart. Please try again.');
      }
    }
  }

  async #addToCart(variantId, quantity = 1) {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity.toString());

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.description || 'Failed to add product to cart');
    }

    // Fetch was successful, run auditzyTrackingScript() if it exists
    try {
      if (typeof window !== 'undefined' && typeof window.auditzyTrackingScript === 'function') {
        window.auditzyTrackingScript();
      } else if (typeof auditzyTrackingScript === 'function') {
        auditzyTrackingScript();
      }
    } catch (err) {
      console.warn('auditzyTrackingScript failed:', err);
    }

    const data = await response.json();
    // NOTE: /cart/add.js returns the added LINE ITEM, NOT the full cart.
    // Do NOT overwrite window.latestCartData here — it will be refreshed
    // by #fetchCartData() which calls /cart.js for the full cart object.
    return data;
  }

  #isInCart(variantId) {
    if (!variantId) return false;
    const items = window.latestCartData?.items || [];
    return items.some((item) => (item?.variant_id ?? '').toString() === variantId.toString());
  }

  /**
   * Update card button text (SELECT / SELECTED / ADDED / SOLD OUT).
   * ADDED is only shown when variantInCartFromDrawer === true, which is set only when
   * we know the cart drawer was just updated (syncCartSelectionWithCards calls #isInCart then passes result here).
   */
  #updateButtonState(card, isCurrent, isSelected, isExhaustedInCart = false, variantInCartFromDrawer = undefined) {
    const button = card.querySelector('.complementary-product-card__select-button');
    if (!button) return;

    const buttonText = button.querySelector('.complementary-product-card__button-text');
    if (!buttonText) return;

    const productId = card.dataset.productId;

    if (isExhaustedInCart) {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      buttonText.textContent = 'SOLD OUT';
      card.dataset.selected = 'false';
      card.dataset.soldOut = 'true';
      return;
    }

    const isDisabled = button.disabled || button.getAttribute('aria-disabled') === 'true';

    if (isDisabled) {
      buttonText.textContent = 'SOLD OUT';
      card.dataset.selected = 'false';
      card.dataset.soldOut = 'true';
    } else if (variantInCartFromDrawer === true) {
      // Only when cart drawer was updated and #isInCart returned true → show ADDED
      buttonText.textContent = 'ADDED';
      card.dataset.selected = 'false';
      card.dataset.soldOut = 'false';
      const btn = document.querySelectorAll('.custom-atc-btn');
      btn.forEach(btn => {
        btn.innerText = 'View Cart';
        btn.dataset.currentAction = 'open cart';
      });
    } else if (isSelected) {
      buttonText.textContent = 'SELECTED';
      card.dataset.selected = 'true';
      card.dataset.soldOut = 'false';
    } else {
      buttonText.textContent = 'SELECT';
      card.dataset.selected = 'false';
      card.dataset.soldOut = 'false';
    }
  }

  #updateTotal() {
    if (!this.refs.totalPrice) return;

    let total = 0;
    let hasAvailableProducts = false;

    this.#selectedProducts.forEach((productId) => {
      // Only include available products that are not inventory-exhausted in the total
      if (!this.#unavailableProducts.has(productId) && !this.#inventoryExhaustedProducts.has(productId)) {
        const price = this.#productPrices.get(productId);
        if (price) {
          total += price;
          hasAvailableProducts = true;
        }
      }
    });

    const formattedTotal = this.#formatPrice(total);
    this.refs.totalPrice.textContent = formattedTotal;

    // Disable buy button if no available products are selected
    if (this.refs.buyButton) {
      if (!hasAvailableProducts || this.#selectedProducts.size === 0) {
        this.refs.buyButton.disabled = true;
        this.refs.buyButton.setAttribute('aria-disabled', 'true');
      } else {
        this.refs.buyButton.disabled = false;
        this.refs.buyButton.removeAttribute('aria-disabled');
      }
    }

    // Hide/show footer based on whether any product is purchasable
    this.#updateFooterVisibility();
  }

  #updateFooterVisibility() {
    if (!this.refs.footer) return;

    let hasAnyPurchasable = false;

    if (this.refs.productCard) {
      this.refs.productCard.forEach((card) => {
        const productId = card.dataset.productId;
        if (
          productId &&
          !this.#unavailableProducts.has(productId) &&
          !this.#inventoryExhaustedProducts.has(productId)
        ) {
          hasAnyPurchasable = true;
        }
      });
    }

    if (hasAnyPurchasable) {
      this.refs.footer.style.display = '';
      this.refs.footer.style.opacity = '1';
      this.refs.footer.style.pointerEvents = '';
    } else {
      this.refs.footer.style.display = 'none';
      this.refs.footer.style.opacity = '0';
      this.refs.footer.style.pointerEvents = 'none';
    }
  }

  #formatPrice(price) {
    const amount = price / 100;
    const formatted = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
    return `₹ ${formatted}`;
  }
}

if (!customElements.get('complementary-products')) {
  customElements.define('complementary-products', ComplementaryProducts);
}
